import { useRef, useState } from "react";
import type {
  CSSProperties,
  KeyboardEvent,
  PointerEvent as ReactPointerEvent,
  ReactNode,
} from "react";
import {
  MIN_BLOCK_MINUTES,
  formatClock,
  mergeTimeEntries,
  moveTimeEntry,
  resizeBoundary,
  resizeDayEdge,
  snapMinutes,
  splitTimeEntryAt,
  type TimeEntry,
} from "./time-blocks";

interface TimeTimelineProps {
  entries: TimeEntry[];
  activeDate: string;
  onActiveDate: (date: string) => void;
  onChange: (entries: TimeEntry[]) => void;
  activeDetails: ReactNode;
}

interface TimelineRange {
  start: number;
  end: number;
}

interface MergePrompt {
  date: string;
  leftId: string;
  rightId: string;
}

interface LongPressState {
  pointerId: number;
  startX: number;
  startY: number;
  timer: number;
  triggered: boolean;
}

const LONG_PRESS_MS = 600;
const PRESS_MOVE_TOLERANCE = 8;
const MIN_RANGE_MINUTES = 4 * 60;
const DAY_EDGE_PADDING_MINUTES = 2 * 60;

function dateLabel(date: string) {
  return new Date(`${date}T12:00:00`).toLocaleDateString("fi-FI", {
    weekday: "short",
    day: "numeric",
    month: "numeric",
  });
}

function getRange(entries: TimeEntry[]): TimelineRange {
  const first = Math.min(...entries.map((entry) => entry.startMinutes));
  const last = Math.max(...entries.map((entry) => entry.endMinutes));
  const start = Math.max(0, first - DAY_EDGE_PADDING_MINUTES);
  const end = Math.min(24 * 60, Math.max(
    start + MIN_RANGE_MINUTES,
    last + DAY_EDGE_PADDING_MINUTES,
  ));
  return { start, end };
}

function percent(minute: number, range: { start: number; end: number }) {
  return ((minute - range.start) / (range.end - range.start)) * 100;
}

function minuteFromPointer(
  event: ReactPointerEvent<HTMLButtonElement>,
  range: TimelineRange,
) {
  const track = event.currentTarget.closest<HTMLElement>(".time-track");
  if (!track) return range.start;
  const rect = track.getBoundingClientRect();
  const ratio = Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width));
  return range.start + ratio * (range.end - range.start);
}

function sliderKeys(event: KeyboardEvent<HTMLButtonElement>, current: number, update: (minute: number) => void) {
  if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
  event.preventDefault();
  update(current + (event.key === "ArrowRight" ? MIN_BLOCK_MINUTES : -MIN_BLOCK_MINUTES));
}

export function TimeTimeline({
  entries,
  activeDate,
  onActiveDate,
  onChange,
  activeDetails,
}: TimeTimelineProps) {
  const dates = Array.from(new Set(entries.map((entry) => entry.date))).sort();
  const [ranges, setRanges] = useState<Record<string, TimelineRange>>({});
  const [mergePrompt, setMergePrompt] = useState<MergePrompt | null>(null);
  const longPressRef = useRef<LongPressState | null>(null);
  const suppressClickRef = useRef(false);
  const scaleDragRef = useRef<{ pointerId: number; range: TimelineRange } | null>(null);
  const blockDragRef = useRef<{
    pointerId: number;
    id: string;
    startX: number;
    startY: number;
    grabOffset: number;
    range: TimelineRange;
    dragging: boolean;
  } | null>(null);
  const [draggingBlockId, setDraggingBlockId] = useState<string | null>(null);

  const beginLongPress = (
    event: ReactPointerEvent<HTMLButtonElement>,
    action: () => void,
    suppressClick = false,
  ) => {
    if (longPressRef.current) window.clearTimeout(longPressRef.current.timer);
    const state: LongPressState = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      timer: 0,
      triggered: false,
    };
    state.timer = window.setTimeout(() => {
      if (longPressRef.current !== state) return;
      state.triggered = true;
      suppressClickRef.current = suppressClick;
      action();
    }, LONG_PRESS_MS);
    longPressRef.current = state;
  };

  const longPressMoved = (event: ReactPointerEvent<HTMLButtonElement>) => {
    const state = longPressRef.current;
    if (!state || state.pointerId !== event.pointerId) return true;
    if (state.triggered) return false;
    const moved = Math.hypot(event.clientX - state.startX, event.clientY - state.startY)
      > PRESS_MOVE_TOLERANCE;
    if (moved) {
      window.clearTimeout(state.timer);
      longPressRef.current = null;
    }
    return moved;
  };

  const finishLongPress = (pointerId: number) => {
    const state = longPressRef.current;
    if (!state || state.pointerId !== pointerId) return;
    window.clearTimeout(state.timer);
    longPressRef.current = null;
  };

  const updateRangeEdge = (
    date: string,
    dayEntries: TimeEntry[],
    current: TimelineRange,
    edge: "start" | "end",
    nextMinute: number,
  ) => {
    const first = dayEntries[0].startMinutes;
    const last = dayEntries.at(-1)!.endMinutes;
    setRanges((previous) => {
      const range = previous[date] ?? current;
      if (edge === "start") {
        const latestStart = Math.min(
          Math.max(0, first - MIN_BLOCK_MINUTES),
          range.end - MIN_RANGE_MINUTES,
        );
        return {
          ...previous,
          [date]: {
            ...range,
            start: Math.max(0, Math.min(snapMinutes(nextMinute), latestStart)),
          },
        };
      }
      const earliestEnd = Math.max(
        Math.min(24 * 60, last + MIN_BLOCK_MINUTES),
        range.start + MIN_RANGE_MINUTES,
      );
      return {
        ...previous,
        [date]: {
          ...range,
          end: Math.min(24 * 60, Math.max(snapMinutes(nextMinute), earliestEnd)),
        },
      };
    });
  };

  return (
    <section className="week-timeline" aria-labelledby="time-timeline-heading">
      <div className="timeline-heading">
        <div>
          <span className="eyebrow">Muokattava työpäivä</span>
          <h2 id="time-timeline-heading">Venytä, jaa tai yhdistä blokkeja</h2>
        </div>
        <p>
          Raahaa blokkia siirtääksesi sitä ja reunoja muuttaaksesi päivän pituutta
          enintään kahden tunnin liikkumavarassa. Pitkä painallus jakaa tai yhdistää.
        </p>
      </div>
      <div className="timeline-days">
        {dates.map((date) => {
          const dayEntries = entries
            .filter((entry) => entry.date === date)
            .sort((a, b) => a.startMinutes - b.startMinutes);
          const range = ranges[date] ?? getRange(dayEntries);
          const markers = Array.from(
            { length: Math.floor((range.end - range.start) / 60) + 1 },
            (_, index) => range.start + index * 60,
          );
          const updateOuter = (id: string, edge: "start" | "end", minute: number) =>
            onChange(resizeDayEdge(
              entries,
              id,
              edge,
              Math.max(range.start, Math.min(range.end, minute)),
            ));
          const updateDivider = (leftId: string, rightId: string, minute: number) =>
            onChange(resizeBoundary(entries, leftId, rightId, minute));
          const selected = activeDate === date;
          const dayMergePrompt = mergePrompt?.date === date ? mergePrompt : null;
          const mergeLeft = dayMergePrompt
            ? dayEntries.find((entry) => entry.id === dayMergePrompt.leftId)
            : null;
          const mergeRight = dayMergePrompt
            ? dayEntries.find((entry) => entry.id === dayMergePrompt.rightId)
            : null;

          return (
            <article className={selected ? "timeline-day selected" : "timeline-day"} key={date}>
              <button className="timeline-date-button" type="button" onClick={() => onActiveDate(date)}>
                <strong>{dateLabel(date)}</strong>
                <span>{dayEntries.length} {dayEntries.length === 1 ? "blokki" : "blokkia"}</span>
              </button>
              <div className="timeline-scroll">
                <div
                  className="time-track"
                  aria-label={`${dateLabel(date)} aikajana`}
                  onClick={() => onActiveDate(date)}
                >
                  <div className="timeline-scale" aria-hidden="true">
                    {markers.map((minute) => (
                      <span key={minute} style={{ left: `${percent(minute, range)}%` }}>
                        {formatClock(minute)}
                      </span>
                    ))}
                  </div>
                  <button
                    className="timeline-range-handle start"
                    type="button"
                    role="slider"
                    aria-label={`${dateLabel(date)} näkyvän aikajanan alku`}
                    aria-valuemin={0}
                    aria-valuemax={Math.max(0, Math.min(
                      dayEntries[0].startMinutes - MIN_BLOCK_MINUTES,
                      range.end - MIN_RANGE_MINUTES,
                    ))}
                    aria-valuenow={range.start}
                    aria-valuetext={formatClock(range.start)}
                    style={{ left: "0%" }}
                    onClick={(event) => event.stopPropagation()}
                    onKeyDown={(event) =>
                      sliderKeys(event, range.start, (minute) =>
                        updateRangeEdge(date, dayEntries, range, "start", minute))}
                    onPointerDown={(event) => {
                      event.stopPropagation();
                      scaleDragRef.current = { pointerId: event.pointerId, range };
                      event.currentTarget.setPointerCapture(event.pointerId);
                    }}
                    onPointerMove={(event) => {
                      const drag = scaleDragRef.current;
                      if (drag?.pointerId === event.pointerId
                        && event.currentTarget.hasPointerCapture(event.pointerId)) {
                        updateRangeEdge(
                          date,
                          dayEntries,
                          range,
                          "start",
                          minuteFromPointer(event, drag.range),
                        );
                      }
                    }}
                    onPointerUp={(event) => {
                      scaleDragRef.current = null;
                      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
                        event.currentTarget.releasePointerCapture(event.pointerId);
                      }
                    }}
                    onPointerCancel={() => {
                      scaleDragRef.current = null;
                    }}
                  >
                    <span />
                  </button>
                  <button
                    className="timeline-range-handle end"
                    type="button"
                    role="slider"
                    aria-label={`${dateLabel(date)} näkyvän aikajanan loppu`}
                    aria-valuemin={Math.min(24 * 60, Math.max(
                      dayEntries.at(-1)!.endMinutes + MIN_BLOCK_MINUTES,
                      range.start + MIN_RANGE_MINUTES,
                    ))}
                    aria-valuemax={24 * 60}
                    aria-valuenow={range.end}
                    aria-valuetext={formatClock(range.end)}
                    style={{ left: "100%" }}
                    onClick={(event) => event.stopPropagation()}
                    onKeyDown={(event) =>
                      sliderKeys(event, range.end, (minute) =>
                        updateRangeEdge(date, dayEntries, range, "end", minute))}
                    onPointerDown={(event) => {
                      event.stopPropagation();
                      scaleDragRef.current = { pointerId: event.pointerId, range };
                      event.currentTarget.setPointerCapture(event.pointerId);
                    }}
                    onPointerMove={(event) => {
                      const drag = scaleDragRef.current;
                      if (drag?.pointerId === event.pointerId
                        && event.currentTarget.hasPointerCapture(event.pointerId)) {
                        updateRangeEdge(
                          date,
                          dayEntries,
                          range,
                          "end",
                          minuteFromPointer(event, drag.range),
                        );
                      }
                    }}
                    onPointerUp={(event) => {
                      scaleDragRef.current = null;
                      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
                        event.currentTarget.releasePointerCapture(event.pointerId);
                      }
                    }}
                    onPointerCancel={() => {
                      scaleDragRef.current = null;
                    }}
                  >
                    <span />
                  </button>
                  {dayEntries.map((entry, index) => {
                    const style = {
                      "--block-left": `${percent(entry.startMinutes, range)}%`,
                      "--block-width": `${percent(entry.endMinutes, range) - percent(entry.startMinutes, range)}%`,
                    } as CSSProperties;
                    return (
                      <button
                        className={[
                          "timeline-block",
                          `block-tone-${(index % 3) + 1}`,
                          draggingBlockId === entry.id ? "dragging" : "",
                        ].filter(Boolean).join(" ")}
                        style={style}
                        type="button"
                        key={entry.id}
                        title="Paina pitkään jakaaksesi blokin tästä kohdasta"
                        onClick={(event) => {
                          if (suppressClickRef.current) {
                            suppressClickRef.current = false;
                            event.stopPropagation();
                            return;
                          }
                          onActiveDate(date);
                        }}
                        onKeyDown={(event) => {
                          if (event.key === "Enter" && event.shiftKey) {
                            event.preventDefault();
                            onChange(splitTimeEntryAt(
                              entries,
                              entry.id,
                              `time-${crypto.randomUUID()}`,
                              (entry.startMinutes + entry.endMinutes) / 2,
                            ));
                            return;
                          }
                          if (event.altKey
                            && (event.key === "ArrowLeft" || event.key === "ArrowRight")) {
                            event.preventDefault();
                            onChange(moveTimeEntry(
                              entries,
                              entry.id,
                              entry.startMinutes
                                + (event.key === "ArrowRight"
                                  ? MIN_BLOCK_MINUTES
                                  : -MIN_BLOCK_MINUTES),
                            ));
                          }
                        }}
                        onPointerDown={(event) => {
                          event.currentTarget.setPointerCapture(event.pointerId);
                          const minute = minuteFromPointer(event, range);
                          setRanges((previous) => ({ ...previous, [date]: range }));
                          blockDragRef.current = {
                            pointerId: event.pointerId,
                            id: entry.id,
                            startX: event.clientX,
                            startY: event.clientY,
                            grabOffset: minute - entry.startMinutes,
                            range,
                            dragging: false,
                          };
                          beginLongPress(event, () => {
                            onActiveDate(date);
                            onChange(splitTimeEntryAt(
                              entries,
                              entry.id,
                              `time-${crypto.randomUUID()}`,
                              minute,
                            ));
                          }, true);
                        }}
                        onPointerMove={(event) => {
                          const drag = blockDragRef.current;
                          if (!drag
                            || drag.pointerId !== event.pointerId
                            || drag.id !== entry.id) return;
                          const moved = longPressMoved(event);
                          if (!moved) return;
                          if (!drag.dragging) {
                            drag.dragging = true;
                            suppressClickRef.current = true;
                            setDraggingBlockId(entry.id);
                          }
                          onActiveDate(date);
                          onChange(moveTimeEntry(
                            entries,
                            entry.id,
                            minuteFromPointer(event, drag.range) - drag.grabOffset,
                          ));
                        }}
                        onPointerUp={(event) => {
                          finishLongPress(event.pointerId);
                          blockDragRef.current = null;
                          setDraggingBlockId(null);
                          if (event.currentTarget.hasPointerCapture(event.pointerId)) {
                            event.currentTarget.releasePointerCapture(event.pointerId);
                          }
                        }}
                        onPointerCancel={(event) => {
                          finishLongPress(event.pointerId);
                          blockDragRef.current = null;
                          setDraggingBlockId(null);
                        }}
                        aria-label={`${formatClock(entry.startMinutes)}–${formatClock(entry.endMinutes)}, ${entry.description || "ei työnselitystä"}. Raahaa siirtääksesi tai paina pitkään jakaaksesi.`}
                      >
                        <strong>{formatClock(entry.startMinutes)}–{formatClock(entry.endMinutes)}</strong>
                        <span>{entry.description || entry.workNumber || "Työblokki"}</span>
                      </button>
                    );
                  })}
                  {dayEntries[0] && (
                    <button
                      className="timeline-handle outer start"
                      type="button"
                      role="slider"
                      aria-label={`${dateLabel(date)} aloitusaika`}
                      aria-valuemin={range.start}
                      aria-valuemax={dayEntries[0].endMinutes - MIN_BLOCK_MINUTES}
                      aria-valuenow={dayEntries[0].startMinutes}
                      aria-valuetext={formatClock(dayEntries[0].startMinutes)}
                      style={{ left: `${percent(dayEntries[0].startMinutes, range)}%` }}
                      onKeyDown={(event) =>
                        sliderKeys(event, dayEntries[0].startMinutes, (minute) =>
                          updateOuter(dayEntries[0].id, "start", minute))}
                      onPointerDown={(event) => event.currentTarget.setPointerCapture(event.pointerId)}
                      onPointerMove={(event) => {
                        if (event.currentTarget.hasPointerCapture(event.pointerId)) {
                          updateOuter(dayEntries[0].id, "start", minuteFromPointer(event, range));
                        }
                      }}
                      onPointerUp={(event) => {
                        if (event.currentTarget.hasPointerCapture(event.pointerId)) {
                          event.currentTarget.releasePointerCapture(event.pointerId);
                        }
                      }}
                    >
                      <span />
                    </button>
                  )}
                  {dayEntries.slice(0, -1).map((left, index) => {
                    const right = dayEntries[index + 1];
                    return (
                      <button
                        className="timeline-handle divider"
                        type="button"
                        role="slider"
                        key={`${left.id}-${right.id}`}
                        aria-label={`${dateLabel(date)} blokkien jakokohta ${index + 1}. Paina pitkään yhdistääksesi.`}
                        aria-valuemin={left.startMinutes + MIN_BLOCK_MINUTES}
                        aria-valuemax={right.endMinutes - MIN_BLOCK_MINUTES}
                        aria-valuenow={left.endMinutes}
                        aria-valuetext={formatClock(left.endMinutes)}
                        style={{ left: `${percent(left.endMinutes, range)}%` }}
                        onKeyDown={(event) =>
                          sliderKeys(event, left.endMinutes, (minute) =>
                            updateDivider(left.id, right.id, minute))}
                        title="Paina pitkään yhdistääksesi blokit"
                        onPointerDown={(event) => {
                          event.currentTarget.setPointerCapture(event.pointerId);
                          beginLongPress(event, () => {
                            onActiveDate(date);
                            setMergePrompt({
                              date,
                              leftId: left.id,
                              rightId: right.id,
                            });
                          });
                        }}
                        onPointerMove={(event) => {
                          const shouldResize = longPressMoved(event);
                          if (shouldResize && event.currentTarget.hasPointerCapture(event.pointerId)) {
                            updateDivider(left.id, right.id, minuteFromPointer(event, range));
                          }
                        }}
                        onPointerUp={(event) => {
                          finishLongPress(event.pointerId);
                          if (event.currentTarget.hasPointerCapture(event.pointerId)) {
                            event.currentTarget.releasePointerCapture(event.pointerId);
                          }
                        }}
                        onPointerCancel={(event) => finishLongPress(event.pointerId)}
                      >
                        <span />
                      </button>
                    );
                  })}
                  {dayEntries.at(-1) && (
                    <button
                      className="timeline-handle outer end"
                      type="button"
                      role="slider"
                      aria-label={`${dateLabel(date)} lopetusaika`}
                      aria-valuemin={dayEntries.at(-1)!.startMinutes + MIN_BLOCK_MINUTES}
                      aria-valuemax={range.end}
                      aria-valuenow={dayEntries.at(-1)!.endMinutes}
                      aria-valuetext={formatClock(dayEntries.at(-1)!.endMinutes)}
                      style={{ left: `${percent(dayEntries.at(-1)!.endMinutes, range)}%` }}
                      onKeyDown={(event) =>
                        sliderKeys(event, dayEntries.at(-1)!.endMinutes, (minute) =>
                          updateOuter(dayEntries.at(-1)!.id, "end", minute))}
                      onPointerDown={(event) => event.currentTarget.setPointerCapture(event.pointerId)}
                      onPointerMove={(event) => {
                        if (event.currentTarget.hasPointerCapture(event.pointerId)) {
                          updateOuter(dayEntries.at(-1)!.id, "end", minuteFromPointer(event, range));
                        }
                      }}
                      onPointerUp={(event) => {
                        if (event.currentTarget.hasPointerCapture(event.pointerId)) {
                          event.currentTarget.releasePointerCapture(event.pointerId);
                        }
                      }}
                    >
                      <span />
                    </button>
                  )}
                </div>
                {dayMergePrompt && mergeLeft && mergeRight && (
                  <div className="time-merge-choice" role="dialog" aria-label="Yhdistä työblokit">
                    <div>
                      <strong>Yhdistä blokit</strong>
                      <span>Valitse kumman blokin tiedot säilytetään.</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        onChange(mergeTimeEntries(
                          entries,
                          dayMergePrompt.leftId,
                          dayMergePrompt.rightId,
                          dayMergePrompt.leftId,
                        ));
                        setMergePrompt(null);
                      }}
                    >
                      Säilytä {formatClock(mergeLeft.startMinutes)}–{formatClock(mergeLeft.endMinutes)} tiedot
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        onChange(mergeTimeEntries(
                          entries,
                          dayMergePrompt.leftId,
                          dayMergePrompt.rightId,
                          dayMergePrompt.rightId,
                        ));
                        setMergePrompt(null);
                      }}
                    >
                      Säilytä {formatClock(mergeRight.startMinutes)}–{formatClock(mergeRight.endMinutes)} tiedot
                    </button>
                    <button className="merge-cancel" type="button" onClick={() => setMergePrompt(null)}>
                      Peruuta
                    </button>
                  </div>
                )}
              </div>
              {selected && (
                <div className="timeline-day-details">
                  {activeDetails}
                </div>
              )}
            </article>
          );
        })}
      </div>
    </section>
  );
}
